import { configured } from './lib/supabase'
import { useAttentionCount, useAlerts, useRecipes, useProfileRow } from './lib/queries'
import { shouldOnboard } from './lib/onboarding'
import { needsYou } from './lib/attention'
import { useAuth } from './lib/auth'
import { Suspense, lazy, memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { flushSync } from 'react-dom'
import { useRoute, goHash, tabTarget, gateScreen, TAB_RESET, TABS, type Screen, type Tab } from './lib/router'
import { useIsAdmin, adminGate } from './lib/useIsAdmin'
import { AccountSheet } from './components/AccountSheet'
import { AccountContext } from './components/ScreenHead'
import { TabBar, Toast } from './ui'
import { House, Compass, MessageCircle, List, Calendar } from 'lucide-react'
import { SignIn } from './screens/SignIn'
import { Onboarding } from './screens/Onboarding'
import { Today as TodayScreen } from './screens/Today'
import { Browse as BrowseScreen } from './screens/Browse'
import { Butler as ButlerScreen } from './screens/Butler'
import { Lists as ListsScreen } from './screens/Lists'
import { Year as YearScreen } from './screens/Year'
import { City } from './screens/City'
import { Shell } from './desk/Shell'
import { Boundary } from './components/Boundary'
import { DESK_MIN, isDesk } from './lib/desk'

// A stored session means the shell should paint immediately rather than wait
// for `getSession()` to resolve it — the same answer, just not yet confirmed.
// A missing or unreadable key means there is nothing to optimistically show.
function hasStoredSession() {
  try {
    const url = import.meta.env.VITE_SUPABASE_URL as string | undefined
    if (!url) return false
    const ref = new URL(url).hostname.split('.')[0]
    return localStorage.getItem(`sb-${ref}-auth-token`) != null
  } catch {
    return false
  }
}

/**
 * a4-13: the five tabs, memoised. Every one stays mounted (X7), and none takes
 * a prop that changes with the tab, so a tab switch re-rendering `App` had no
 * reason to re-render all five: the profile at CPU x4 had Lists at ~210 ms,
 * Today and Year at ~140 ms each and Ask's thread at ~100 ms of every switch,
 * on screens nobody could see. A screen still re-renders on its own when a query
 * it reads changes, and Browse still re-renders on the route, which it reads
 * itself (`useRoute()`). Lists takes only `openId`, a string: a function prop
 * built inline would be new every render and defeat `memo`, which is why it
 * writes the route itself (`replace`) rather than being handed a callback.
 */
const Today = memo(TodayScreen)
const Browse = memo(BrowseScreen)
const Butler = memo(ButlerScreen)
const Lists = memo(ListsScreen)
const Year = memo(YearScreen)

/**
 * The component specimen, split out of the main chunk. 4.1 kB gzipped that a
 * page nobody but a design pass opens has no business putting in the bundle
 * every cold start downloads.
 *
 * Only its own code moves. `ui/index.ts` is a barrel this file already imports
 * for `Toast`, which puts the barrel in the entry chunk, and every primitive it
 * re-exports goes with it — so the fourteen and their three libraries are in
 * the main chunk whatever this line does. That is the right answer anyway: in
 * WP 2.5 the screens use them, and the measurement in the report is what the
 * app will really pay.
 */
const DevUi = lazy(() => import('./screens/DevUi').then(m => ({ default: m.DevUi })))

const TAB_ICON: Record<Tab, ReactNode> = {
  today: <House aria-hidden="true" />,
  browse: <Compass aria-hidden="true" />,
  butler: <MessageCircle aria-hidden="true" />,
  lists: <List aria-hidden="true" />,
  year: <Calendar aria-hidden="true" />,
}
const TAB_LABEL: Record<Tab, string> = {
  today: 'Today', browse: 'Browse', butler: 'Ask', lists: 'Lists', year: 'Year',
}

export default function App() {
  const { session, loading, setup } = useAuth()
  const route = useRoute()
  // U0.3: `#/dev/ui` is an admin screen. Not an admin (or not yet known) draws
  // Today; the hash stays, so Doug's reload on it lands as the answer arrives.
  const isAdmin = useIsAdmin()
  const tab = gateScreen(route.tab, isAdmin)
  const { id: routeId } = route
  const [account, setAccount] = useState(false)
  // W0: the width switch. Above the session gate, so the listener is installed
  // on the first render and not only once a session has resolved.
  const [desk, setDesk] = useState(() => isDesk(window.innerWidth))
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${DESK_MIN}px)`)
    const on = () => setDesk(mq.matches)
    on()
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])

  // N2/B3: the badge follows what needs a decision — open tasks due today
  // plus saved events in the next 48 hours — not the whole saved backlog.
  // See `useAttentionCount`.
  const attentionCount = useAttentionCount()

  // N1 (modify): one dot, and only for these two reasons. `useAlerts` already
  // reads unacknowledged rows only, so its length alone answers "an alert is
  // unacknowledged". The two `probe-` recipes are failure rigs kept broken
  // and blocked on purpose (see `HealthStrip`'s own note) — counting them
  // would light this dot permanently, which is exactly the busyness Doug
  // asked this package to remove.
  const recipes = useRecipes().data ?? []
  const alerts = useAlerts().data ?? []
  // U0.3: the dot means "the harvest needs a person", which only an admin can
  // act on; for anyone else it would light the Account button over a System
  // row they cannot see.
  const showDot = adminGate(isAdmin) === 'show' && (alerts.length > 0
    || recipes.some(r => !r.id.startsWith('probe-') && (r.status === 'broken' || r.status === 'blocked')))

  // WP 10.1: the button is drawn by each screen's `ScreenHead` now, not by a
  // bar here; the sheet's open state and the dot's rule stay in this file
  // and reach it through context (see `ScreenHead.tsx` for why not props).
  // Memoised so a render of the shell that changes neither does not
  // re-render five headers for nothing.
  const accountCtx = useMemo(() => ({ open: () => setAccount(true), dot: showDot }), [showDot])

  // D6: a tab switch cross-fades rather than cuts. `flushSync` is what makes
  // there be something for the transition to capture — `go` notifies every
  // mounted `useRoute()` synchronously (WP 3.2: `useSyncExternalStore`'s
  // listeners, called straight away rather than waited on through the
  // browser's own later `hashchange`), but only inside this callback does
  // that happen *before* `startViewTransition` takes its "after" snapshot
  // rather than on the next task. Skipped entirely, not just muted by CSS, when the
  // browser has no support or the device asked for less motion — the stop
  // condition this package's brief names is `startViewTransition` fighting a
  // sheet's history handling, which never gets the chance to happen here
  // since this only ever wraps a tab-bar switch, never `go(tab, id)`.
  //
  // a1-32: a tab comes back as it was left — the last hash it was on, which
  // `lastHash` keeps per tab — and a tap on the tab already showing goes to
  // its index, at the top (`tabTarget`). Scroll was already kept per tab
  // (X7, below); this is the half the hash holds.
  const lastHash = useRef<Partial<Record<Tab, string>>>({})
  useEffect(() => {
    if ((TABS as readonly string[]).includes(route.tab)) lastHash.current[route.tab as Tab] = window.location.hash
  }, [route])

  function switchTab(next: Tab) {
    const { hash, reset } = tabTarget(next, tab, lastHash.current)
    if (reset) {
      // Not a transition: nothing changes tab. The index, at the top, and
      // the screen drops what the hash does not hold.
      goHash(hash)
      scrollOf.current[next] = 0
      window.scrollTo(0, 0)
      window.dispatchEvent(new CustomEvent(TAB_RESET, { detail: next }))
      return
    }
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduced || typeof document.startViewTransition !== 'function') { goHash(hash); return }
    document.startViewTransition(() => flushSync(() => goHash(hash)))
  }

  // X7. Every tab is mounted, so switching back is not a remount — but the
  // document is what scrolls, and a hidden screen contributes no height to it,
  // so the browser clamps the offset the moment the tall one goes away. Keeping
  // the element alive keeps its state, not its scroll position; this keeps the
  // position.
  //
  // A ref rather than state: nothing renders from it, and a passive listener
  // that set state on every scroll event would re-render five screens for a
  // number only this pair of effects reads. `tabRef` is assigned during render
  // and so is already the new tab by the time the layout effect below runs,
  // which is what stops the restoring scroll from being recorded against the
  // tab being left.
  const scrollOf = useRef<Partial<Record<Screen, number>>>({})
  const tabRef = useRef<Screen>(tab)
  tabRef.current = tab
  const firstRender = useRef(true)

  // U1.4: a person whose profile row has no `onboarded_at` sees the first-
  // sign-in screen instead of the shell. Read from the query's *settled*
  // state, never from `DEFAULT_PROFILE` or `undefined` (the cold-load trap:
  // a pending read must draw the app's own loading, not a form), and
  // latched: once the screen is up it stays until `onboarded_at` is written,
  // so a background refetch cannot unmount a half-filled form. The hook sits
  // above every early return below, or it would not run on a first render
  // that returns `Loading`.
  const profileQ = useProfileRow()
  const onboarding = useRef(false)
  if (profileQ.data && shouldOnboard(profileQ.status, profileQ.isFetching, profileQ.data)) onboarding.current = true
  const showOnboarding = onboarding.current && !!session && !!profileQ.data && !profileQ.data.onboarded_at

  useEffect(() => {
    const on = () => { scrollOf.current[tabRef.current] = window.scrollY }
    window.addEventListener('scroll', on, { passive: true })
    return () => window.removeEventListener('scroll', on)
  }, [])

  useLayoutEffect(() => {
    // Not on the first run. The browser does its own scroll restoration on a
    // reload, and scrolling to a position this session has not recorded yet
    // would only ever mean undoing that.
    if (firstRender.current) { firstRender.current = false; return }
    window.scrollTo(0, scrollOf.current[tab] ?? 0)
  }, [tab])

  if (!configured) {
    return (
      <div className="gate"><div className="box">
        <h1>Not configured</h1>
        <p>Copy <code>app/.env.example</code> to <code>app/.env.local</code> and fill in the
        Supabase URL and anon key, then restart the dev server.</p>
      </div></div>
    )
  }

  // U0.6: an invite or recovery link signs the person in; they set a password
  // before they see the app. Ahead of the loading line so a device that
  // already holds a session never flashes the shell first.
  if (setup) return <SignIn />
  if (loading && !hasStoredSession()) return <div className="gate"><div className="box"><p>Loading…</p></div></div>
  if (!loading && !session) return <SignIn />
  if (showOnboarding) return <Boundary where="onboarding"><Onboarding /></Boundary>
  if (desk) return <Shell showDot={showDot} />

  return (
    <>
      <AccountContext.Provider value={accountCtx}>
        <div className="shell">
          {/* WP 10.1: no top bar. The wordmark went (the name is the app
              icon's job) and the account button moved into each tab's own
              first row, `ScreenHead`. The bar was 64px; the shell keeps 12px
              of it as top padding, and each tab's content moved up 48–60px
              (measured: `docs/phone-plan/reports/10.1.md`). */}
          {/* X7. All four drawn, three of them hidden. A tab switch is no longer
              a remount, so a filter you set, a sheet you had open and a health
              strip you expanded are all still there when you come back — and
              because every screen reads the same store, none of them fetches
              anything on the way in.

              `hidden` rather than a conditional render, and a plain wrapper
              with no class of its own so nothing in the stylesheet can win
              against the browser's `[hidden] { display: none }`. That is the
              `.pitem.tapzone` trap in CLAUDE.md, and the way not to repeat it
              is to give the rule nothing to compete with.

              `openId` is handed only to the tab the route is actually on. The
              second hash segment belongs to one screen at a time, and `Lists`
              answers an id it does not recognise by calling `onOpen(null)` —
              so a hidden Lists reading `#/lists/<id>` meant for another screen
              would rewrite the hash out from under it (UI-audit F25's fix,
              firing on the wrong screen). Browse guards the same way from the
              inside (WP 6.3): it reads `route.id` itself rather than being
              handed it, so a hidden Browse sitting on `#/lists/<id>` cannot
              read that id as its own kind or place deep-link. */}
          <main>
            <div hidden={tab !== 'today'}><Boundary where="today"><Today /></Boundary></div>
            <div hidden={tab !== 'browse'}><Boundary where="browse"><Browse /></Boundary></div>
            <div hidden={tab !== 'butler'}><Boundary where="butler"><Butler /></Boundary></div>
            <div hidden={tab !== 'lists'}>
              <Boundary where="lists"><Lists openId={tab === 'lists' ? routeId : null} /></Boundary>
            </div>
            <div hidden={tab !== 'year'}><Boundary where="year"><Year /></Boundary></div>
            {/* WP 3.1. Citywide news, at `#/city` — where Today's three lines
                send you for the rest of them. A route rather than a tab (see
                `EXTRA_ROUTES`), and rendered only when the route is on it: it
                is a destination, not somewhere you land, so there is no state
                in it worth keeping mounted. */}
            {tab === 'city' && <Boundary where="city"><City /></Boundary>}
            {/* WP 2.2. The component specimen, at `#/dev/ui` — not a tab, not
                linked from anywhere, and reachable on the deployed app without a
                build. Rendered only when the route is on it, unlike the five:
                mounting a lazy chunk the way they are mounted would download it
                on every cold start, which is the whole thing the split avoids.
                The Suspense boundary wraps the lazy child alone (WP 1.1's review
                fix), so a pending chunk cannot blank the screen. */}
            {tab === 'dev' && (
              <Boundary where="dev"><Suspense fallback={null}><DevUi /></Suspense></Boundary>
            )}
          </main>
        </div>
      </AccountContext.Provider>

      <TabBar
        items={TABS.map(t => ({
          key: t, label: TAB_LABEL[t], icon: TAB_ICON[t],
          badge: t === 'lists' ? attentionCount : undefined,
          badgeLabel: t === 'lists' ? needsYou(attentionCount) : undefined,
        }))}
        active={tab}
        onChange={key => switchTab(key as Tab)}
      />

      {account && <Boundary where="account"><AccountSheet onClose={() => setAccount(false)} /></Boundary>}

      {/* WP 2.2. One host for every toast in the app, mounted once and outside
          the scrolling shell. `useToast()` reaches it from anywhere without a
          provider — see `ui/Toast.tsx`. Nothing raises a toast yet; the undos
          move onto it in 2.5. */}
      <Toast />
    </>
  )
}
